/**
 * Returns & Refunds Policy Page
 *
 * Detailed return and refund policies for PetMas customers.
 * Includes process steps, conditions, and contact information.
 */

import { Link } from 'react-router-dom';

export function ReturnsPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-primary-dark text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <RefreshIcon />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Devoluciones y Reembolsos
          </h1>
          <p className="text-lg md:text-xl opacity-90 max-w-2xl mx-auto">
            Tu satisfaccion es nuestra prioridad. Conoce nuestras politicas
            de devolucion y reembolso.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Guarantee Banner */}
          <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6 mb-12 text-center">
            <div className="text-4xl mb-2">✅</div>
            <h2 className="text-2xl font-bold text-green-800 mb-2">
              Garantia de Satisfaccion
            </h2>
            <p className="text-green-700">
              7 dias para devoluciones • Reembolso completo • Sin preguntas
            </p>
          </div>

          {/* When Can You Return */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary text-xl">
                📦
              </span>
              ¿Cuando puedo devolver un producto?
            </h2>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckIcon className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Producto equivocado</h4>
                    <p className="text-gray-600 text-sm">Si recibiste un producto diferente al que pediste.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckIcon className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Producto danado</h4>
                    <p className="text-gray-600 text-sm">Si el producto llego en mal estado o con el empaque roto.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckIcon className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Producto vencido o proximo a vencer</h4>
                    <p className="text-gray-600 text-sm">Si el alimento tiene menos de 3 meses de vida util restante.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckIcon className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Defecto de fabrica</h4>
                    <p className="text-gray-600 text-sm">Accesorios con fallas de fabricacion no evidentes al momento de la compra.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckIcon className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Cambio de opinion (productos sin abrir)</h4>
                    <p className="text-gray-600 text-sm">Tienes 7 dias para devolver productos que no hayas abierto.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* What Cannot Be Returned */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center text-red-600 text-xl">
                🚫
              </span>
              ¿Que productos NO se pueden devolver?
            </h2>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <XIcon className="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Alimentos abiertos</h4>
                    <p className="text-gray-600 text-sm">Por razones de higiene y seguridad alimentaria, no aceptamos devoluciones de alimentos que hayan sido abiertos.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <XIcon className="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Productos de higiene usados</h4>
                    <p className="text-gray-600 text-sm">Shampoos, colonias, toallitas y productos similares que hayan sido usados.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <XIcon className="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Placas personalizadas</h4>
                    <p className="text-gray-600 text-sm">Productos grabados con el nombre de tu mascota no pueden devolverse excepto por defectos.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <XIcon className="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Productos con mas de 7 dias</h4>
                    <p className="text-gray-600 text-sm">El plazo para solicitar una devolucion es de 7 dias calendario desde la recepcion.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Return Process */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary text-xl">
                📋
              </span>
              Proceso de Devolucion
            </h2>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gray-200" />

                <div className="space-y-8">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 z-10">
                      1
                    </div>
                    <div className="pt-2">
                      <h4 className="font-semibold text-gray-900 mb-1">Contactanos</h4>
                      <p className="text-gray-600 text-sm">
                        Escribenos por WhatsApp al +51 999 999 999 o al email ayuda@petmas.pe
                        indicando tu numero de pedido y motivo de devolucion.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 z-10">
                      2
                    </div>
                    <div className="pt-2">
                      <h4 className="font-semibold text-gray-900 mb-1">Evaluacion</h4>
                      <p className="text-gray-600 text-sm">
                        Nuestro equipo evaluara tu solicitud en un plazo maximo de 24 horas
                        y te indicara los siguientes pasos.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 z-10">
                      3
                    </div>
                    <div className="pt-2">
                      <h4 className="font-semibold text-gray-900 mb-1">Recojo del Producto</h4>
                      <p className="text-gray-600 text-sm">
                        Coordinaremos el recojo del producto en tu domicilio sin costo adicional
                        (solo en Lima) o te indicaremos donde enviarlo.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 z-10">
                      4
                    </div>
                    <div className="pt-2">
                      <h4 className="font-semibold text-gray-900 mb-1">Reembolso o Cambio</h4>
                      <p className="text-gray-600 text-sm">
                        Una vez verificado el producto, procesaremos tu reembolso en 3-5 dias
                        habiles o te enviaremos el producto de reemplazo.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Refund Methods */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary text-xl">
                💰
              </span>
              Metodos de Reembolso
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h4 className="font-semibold text-gray-900 mb-2">Tarjeta de Credito/Debito</h4>
                <p className="text-gray-600 text-sm">
                  El reembolso se realiza al mismo medio de pago original.
                  Puede tomar de 5-10 dias habiles en reflejarse segun tu banco.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h4 className="font-semibold text-gray-900 mb-2">Transferencia Bancaria</h4>
                <p className="text-gray-600 text-sm">
                  Si pagaste por transferencia, el reembolso se hara a la misma
                  cuenta en 3-5 dias habiles.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h4 className="font-semibold text-gray-900 mb-2">Yape / Plin</h4>
                <p className="text-gray-600 text-sm">
                  Para pagos con billeteras digitales, el reembolso se realiza
                  al mismo numero en 24-48 horas.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h4 className="font-semibold text-gray-900 mb-2">Credito en Tienda</h4>
                <p className="text-gray-600 text-sm">
                  Opcionalmente, puedes elegir recibir credito en tienda
                  + 10% adicional para futuras compras.
                </p>
              </div>
            </div>
          </section>

          {/* Warranty Section */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-primary text-xl">
                🛡️
              </span>
              Garantia de Productos
            </h2>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Categoria
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Periodo de Garantia
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Cobertura
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="px-6 py-4 text-gray-700">Camas y colchones</td>
                    <td className="px-6 py-4 font-medium text-gray-900">30 dias</td>
                    <td className="px-6 py-4 text-gray-600 text-sm">Defectos de costuras y cremalleras</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-700">Transportadoras</td>
                    <td className="px-6 py-4 font-medium text-gray-900">30 dias</td>
                    <td className="px-6 py-4 text-gray-600 text-sm">Defectos estructurales</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-700">Juguetes</td>
                    <td className="px-6 py-4 font-medium text-gray-900">15 dias</td>
                    <td className="px-6 py-4 text-gray-600 text-sm">Defectos de fabricacion</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-700">Comederos/Bebederos</td>
                    <td className="px-6 py-4 font-medium text-gray-900">30 dias</td>
                    <td className="px-6 py-4 text-gray-600 text-sm">Defectos en material</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-gray-700">Electrodomesticos</td>
                    <td className="px-6 py-4 font-medium text-gray-900">6 meses</td>
                    <td className="px-6 py-4 text-gray-600 text-sm">Fallas electricas y mecanicas</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* CTA */}
          <div className="bg-gray-50 rounded-2xl p-8 text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              ¿Necesitas hacer una devolucion?
            </h3>
            <p className="text-gray-600 mb-6">
              Contactanos ahora y te ayudamos con el proceso.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/51999999999?text=Hola,%20necesito%20hacer%20una%20devolucion"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 transition-colors"
              >
                <WhatsAppIcon />
                Iniciar Devolucion
              </a>
              <Link
                to="/ayuda"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-gray-700 font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                Ver Preguntas Frecuentes
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RefreshIcon() {
  return (
    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}
